/** Shared visual config for the Workspace module. */
import { hasPermission } from '../../utils/permissionUtils';

// The logged-in user's users.id, or null.
export const currentUserId = () => {
  try {
    return Number(JSON.parse(localStorage.getItem('userdata') || '{}')?.id) || null;
  } catch {
    return null;
  }
};

// Can the user edit or delete this task? Mirrors PUT / DELETE /tasks/:id:
// the task's creator, or a holder of `delete_tasks` ("Edit/Delete any task").
export const canManageTask = (task) => {
  if (hasPermission('delete_tasks')) return true;
  const me = currentUserId();
  return !!me && task?.created_by != null && Number(task.created_by) === me;
};

// Longest task title — the server refuses more (TASK_TITLE_MAX in
// backend/schemas/workspaceSchemas.js). Used by the task form and Quick add.
export const TASK_TITLE_MAX = 200;

// Longest project name — the server refuses more (PROJECT_NAME_MAX in
// backend/schemas/workspaceSchemas.js). Used by the project form.
export const PROJECT_NAME_MAX = 200;

// Longest team name — the server refuses more (TEAM_NAME_MAX in
// backend/schemas/workspaceSchemas.js). Used by the team form.
export const TEAM_NAME_MAX = 200;

// Longest single time entry — the server refuses more (duration_minutes
// max 1440 in backend/schemas/workspaceSchemas.js).
export const MAX_ENTRY_HOURS = 24;

// What's wrong with an hrs / min pair as typed, or null. Shown live under the
// fields by every "log time" form, so 25 hrs (or 24 h 30 m) is flagged before
// Log is pressed. An empty / zero duration isn't flagged here — forms say so
// on submit, so a blank form doesn't open in red.
export const durationInputError = (hours, minutes) => {
  const h = parseInt(hours, 10) || 0;
  const m = parseInt(minutes, 10) || 0;
  if (h < 0 || m < 0) return "Time can't be negative.";
  if (m > 59) return 'Minutes must be between 0 and 59.';
  if (h > MAX_ENTRY_HOURS) return `Hours can't be more than ${MAX_ENTRY_HOURS}.`;
  if (h * 60 + m > MAX_ENTRY_HOURS * 60) {
    return `A single entry can't be more than ${MAX_ENTRY_HOURS} hours.`;
  }
  return null;
};

// onChange value for an hrs / min field whose spinner arrows loop: stepping up
// past `max` lands on 0, stepping down past 0 lands on `max`. The fields carry
// no min/max attributes — the browser would stop the arrows at the bound, and
// no change event would fire to wrap on. Only a single step across the bound
// wraps; a typed out-of-range value (e.g. 30 hrs) is left for
// durationInputError to flag.
export const wrapSpinValue = (prev, next, max) => {
  if (next === '') return next;
  const p = prev === '' || prev == null ? 0 : Number(prev);
  const n = Number(next);
  if (p === max && n === max + 1) return '0';
  if (p === 0 && n === -1) return String(max);
  return next;
};

export const TASK_STATUS = {
  todo:        { label: 'To Do',       bg: '#f3f4f6', text: '#6b7280', dot: '#9ca3af' },
  in_progress: { label: 'In Progress', bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },
  review:      { label: 'Review',      bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
  done:        { label: 'Done',        bg: '#d1fae5', text: '#065f46', dot: '#10b981' },
};

export const TASK_STATUS_ORDER = ['todo', 'in_progress', 'review', 'done'];

export const PRIORITY = {
  low:    { label: 'Low',    bg: '#f3f4f6', text: '#6b7280' },
  medium: { label: 'Medium', bg: '#e0f2fe', text: '#0369a1' },
  high:   { label: 'High',   bg: '#ffedd5', text: '#c2410c' },
  urgent: { label: 'Urgent', bg: '#fee2e2', text: '#b91c1c' },
};

export const PROJECT_STATUS = {
  planning:  { label: 'Planning',  bg: '#ede9fe', text: '#6d28d9' },
  active:    { label: 'Active',    bg: '#d1fae5', text: '#065f46' },
  on_hold:   { label: 'On Hold',   bg: '#fef3c7', text: '#92400e' },
  completed: { label: 'Completed', bg: '#dbeafe', text: '#1e40af' },
  archived:  { label: 'Archived',  bg: '#f3f4f6', text: '#6b7280' },
};

// Billing / identity option lists (clients + projects).
export const CURRENCIES = [
  { code: 'INR', label: '₹ INR' },
  { code: 'USD', label: '$ USD' },
  { code: 'EUR', label: '€ EUR' },
  { code: 'GBP', label: '£ GBP' },
  { code: 'AED', label: 'د.إ AED' },
  { code: 'AUD', label: '$ AUD' },
  { code: 'CAD', label: '$ CAD' },
  { code: 'SGD', label: '$ SGD' },
];

export const COUNTRY_CODES = [
  { code: '+91', label: '🇮🇳 +91' },
  { code: '+1', label: '🇺🇸 +1' },
  { code: '+44', label: '🇬🇧 +44' },
  { code: '+971', label: '🇦🇪 +971' },
  { code: '+61', label: '🇦🇺 +61' },
  { code: '+65', label: '🇸🇬 +65' },
  { code: '+49', label: '🇩🇪 +49' },
];

export const BUDGET_TYPES = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'retainer', label: 'Retainer' },
];

// Swatch palette for client/team/project colours (shared by ColorSwatchSelect).
export const COLOR_SWATCHES = ['#f54b01', '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#0ea5e9', '#ec4899'];

export const MEMBER_PROGRESS = {
  pending:     { label: 'Pending',     bg: '#f3f4f6', text: '#6b7280' },
  in_progress: { label: 'In Progress', bg: '#dbeafe', text: '#1e40af' },
  review:      { label: 'Review',      bg: '#fef3c7', text: '#92400e' },
  completed:   { label: 'Completed',   bg: '#d1fae5', text: '#065f46' },
};

// Decimal hours — kept for exports/spreadsheets where a number is wanted.
// NOT for on-screen labels: 11 minutes renders as a confusing "0.2".
export const minsToHrs = (mins) => Math.round(((mins || 0) / 60) * 10) / 10;

// Human duration for the UI: 11 → "11m", 90 → "1h 30m", 120 → "2h".
export const fmtDuration = (mins) => {
  const m = Math.max(0, Math.round(Number(mins) || 0));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};

// ── DATETIME handling ───────────────────────────────────────────────────────
// MySQL returns DATETIMEs as naive strings ("YYYY-MM-DD HH:MM:SS") and the DB
// clock is UTC, so these are UTC instants with no zone marker. Parsing one with
// `new Date(str)` would read it as LOCAL time and shift it by the UTC offset
// (the "start/end time is wrong" bug) — always go through these helpers.
const hasZone = (s) => /[zZ]$|[+-]\d{2}:?\d{2}$/.test(String(s).slice(10));

export const utcNaiveToDate = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  if (hasZone(s)) {
    const d = new Date(s);
    return isNaN(d) ? null : d;
  }
  const iso = s.replace(" ", "T");
  const d = new Date(`${iso.length <= 16 ? `${iso}:00` : iso}Z`);
  return isNaN(d) ? null : d;
};

// "YYYY-MM-DDTHH:MM" in the viewer's LOCAL zone, for <input type="datetime-local">.
export const toLocalInputValue = (v) => {
  const d = v instanceof Date ? v : utcNaiveToDate(v);
  const src = d || new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${src.getFullYear()}-${p(src.getMonth() + 1)}-${p(src.getDate())}T${p(src.getHours())}:${p(src.getMinutes())}`;
};

// A datetime-local value (local wall time) → an ISO instant for the API, so the
// server stores an unambiguous UTC point rather than a zone-less string.
export const localInputToIso = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d) ? null : d.toISOString();
};

// Local time-of-day label for a stored UTC datetime, e.g. "10:39 PM".
export const fmtTime = (v) => {
  const d = utcNaiveToDate(v);
  return d ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "";
};

// Local YYYY-MM-DD for a Date (never toISOString, which is UTC).
export const localDateStr = (d = new Date()) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export const initials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '?';

export const AVATAR_COLORS = ['#f54b01', '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444'];
export const avatarColor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

export const fmtDate = (d) => {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date)) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};
